/**
 * Usuários — the BFF's own admins, three cards to a row.
 *
 * Everything on a card comes from the single listing request, roles included. Fetching the roles
 * per user was N+1 from the client and ran into that endpoint's own rate limit — a dozen people
 * on screen was enough to start collecting 429s.
 *
 * Removal is soft: the person leaves the Ativos tab and shows up under Desativados, from where
 * they can be brought back. Nothing is ever deleted — What's New and Tours keep the record of who
 * wrote and who approved them, and that record has to keep resolving to someone.
 *
 * @module Pages/Settings/UserManagement
 */

import { css } from '@emotion/css';
import { UTButton, UTLoading, UTTextField } from '@group-link-one/gl-ui/utilities';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { deleteUser, getUserProfile, getUsers, restoreUser } from '../../Services';
import type { User } from '../../Types/UserManagement.types';
import { DeleteUserModal } from '../DeleteUserModal/DeleteUserModal';
import { UserDetailsModal } from '../Modals/UserDetailsModal';
import { UserCard } from '../UserCard/UserCard';
import { filterUsers, splitUsersByStatus } from '../../Utils/userPresentation';
import { useActionToast } from '@/Hooks/useActionToast';
import { usePrivileges } from '@/Hooks/usePrivileges/usePrivilleges';
import { ManagePermissionsModal } from '@/Pages/Settings/UserOverview/Modals/ManagePermissionsModal';
import { EmptyState } from '@/Shared/components/EmptyState/EmptyState';
import { Pagination } from '@/Shared/components/Pagination/Pagination';
import { Tabs } from '@/Shared/components/Tabs/Tabs';
import { MainContent } from '@/Templates/Main/MainContent/MainContent';
import { useProfile } from '@/Auth/contexts/ProfileContext';
import { USER_DELETE } from '../../Constants/permissions';

const screenStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-base);
`;

const searchStyle = css`
  /* Wide enough for a full name, and the first thing to yield when the header wraps. */
  flex: 0 1 280px;
  min-width: 200px;
`;

const headerStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-base);
`;

const headerTextStyle = css`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const titleStyle = css`
  margin: 0;
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-semibold);
`;

const subtitleStyle = css`
  margin: 0;
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
`;

const spacerStyle = css`
  flex: 1;
`;

/** Three to a row, as the design specifies, collapsing on narrow viewports. */
/* The tab bar already draws the bottom rule, so the pagination rides on it instead of its own row. */
const tabsRowStyle = css`
  display: flex;
  align-items: flex-end;
  gap: var(--space-base);
  border-bottom: 1px solid var(--color-border);

  > *:first-child {
    border-bottom: none;
    margin-bottom: -1px;
  }
`;

const gridStyle = css`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--space-base);

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 760px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

type TabId = 'active' | 'inactive';

/** Two rows of the three-column grid, so a page is a screenful without scrolling to the controls. */
const USERS_PER_PAGE = 6;

const UserList = (): JSX.Element => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { pages: privileges } = usePrivileges();

  const toast = useActionToast();
  const { profile } = useProfile();
  const canDeleteUsers = profile?.permissions?.includes(USER_DELETE) ?? false;
  const [viewingUser, setViewingUser] = useState<User | null>(null);
  const [permissionsUser, setPermissionsUser] = useState<User | null>(null);
  const [deletingUser, setDeletingUser] = useState<User | undefined>();
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [tab, setTab] = useState<TabId>('active');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');

  /*
    One request for everyone, active and deactivated, so switching tabs is instant and the badges
    are real counts rather than "what the current tab happens to hold".
  */
  const { data: users, isLoading } = useQuery({
    queryKey: ['admin', 'users', 'all'],
    queryFn: () => getUsers(true),
  });

  const allUsers = useMemo(() => users ?? [], [users]);

  /*
    Filtered before the split, so the tab badges count the matches: searching a name and seeing
    "Ativos 0 · Desativados 1" is the answer to "where did this person go".
  */
  const matching = useMemo(() => filterUsers(allUsers, search), [allUsers, search]);
  const { active, inactive } = useMemo(() => splitUsersByStatus(matching), [matching]);

  const tabUsers = tab === 'active' ? active : inactive;

  /*
    Paged here and not on the server: `GET /v1/admin/users` returns everyone in one response, with
    no page parameter. The whole list is already in memory, so slicing it is the honest way to page
    it — and the counts on the tabs stay the real totals rather than "what this page holds".
  */
  const userList = useMemo(
    () => tabUsers.slice((page - 1) * USERS_PER_PAGE, page * USERS_PER_PAGE),
    [tabUsers, page],
  );

  // Switching tabs from page 3 would otherwise land on a page the other tab may not have.
  useEffect(() => {
    setPage(1);
  }, [tab, search]);

  /** The details modal needs the full profile, which the listing does not carry. */
  const { data: viewingProfile, isLoading: isLoadingProfile } = useQuery({
    queryKey: ['admin', 'users', viewingUser?.id, 'profile'],
    queryFn: () => getUserProfile(viewingUser?.id ?? ''),
    enabled: viewingUser !== null,
  });

  const removeUser = useMutation({
    mutationFn: deleteUser,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      toast.success('Usuário desativado');
      setDeletingUser(undefined);
      setDeleteError(null);
      // Show where the person went, instead of leaving the screen looking like a delete.
      setTab('inactive');
    },
    onError: (error: Error) => {
      setDeleteError(error.message || 'Não foi possível desativar o usuário.');
      toast.failure('Não foi possível desativar o usuário', error);
    },
  });

  const bringUserBack = useMutation({
    mutationFn: restoreUser,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      toast.success('Usuário reativado');
      setTab('active');
    },
    onError: (error: Error) => {
      toast.failure('Não foi possível reativar o usuário', error);
    },
  });

  if (isLoading) {
    return (
      <MainContent id='users-main' styling={{ noBackgroundColor: true, noBorder: true }}>
        <UTLoading type='dots' />
      </MainContent>
    );
  }

  return (
    <MainContent id='users-main' styling={{ noBackgroundColor: true, noBorder: true }}>
      <div className={screenStyle}>
        <div className={headerStyle}>
          <div className={headerTextStyle}>
            <h2 className={titleStyle}>Usuários do BFF</h2>
            <p className={subtitleStyle}>Quem pode criar, aprovar e administrar.</p>
          </div>
          <div className={spacerStyle} />
          <div className={searchStyle}>
            <UTTextField
              id='users-search'
              placeholder='Buscar por nome ou e-mail'
              value={search}
              leftIcon='Misc.MagnifyingGlass'
              onChange={event => {
                setSearch(event.target.value);
              }}
            />
          </div>
          {privileges.SETTINGS.USERS.CREATE ? (
            <UTButton
              fitContent
              id='users-create'
              text='Novo usuário'
              icon='Arrows.Plus'
              onClick={() => {
                navigate('/settings/users/create');
              }}
            />
          ) : null}
        </div>

        <div className={tabsRowStyle}>
          <Tabs
            activeId={tab}
            items={[
              { id: 'active', label: 'Ativos', badge: active.length },
              { id: 'inactive', label: 'Desativados', badge: inactive.length },
            ]}
            onChange={id => {
              setTab(id as TabId);
            }}
          />
          <div className={spacerStyle} />
          <Pagination
            id='users'
            page={page}
            pageSize={USERS_PER_PAGE}
            total={tabUsers.length}
            onChange={setPage}
          />
        </div>

        {userList.length === 0 ? (
          <EmptyState
            id='users-empty'
            icon={search.trim() ? 'Misc.MagnifyingGlass' : 'Users.Users'}
            /*
              A search that finds nothing is not an empty list — and the other tab may well hold
              the person, which is what the badges are for.
            */
            title={
              search.trim()
                ? `Ninguém encontrado para "${search.trim()}"`
                : tab === 'active'
                  ? 'Nenhum usuário cadastrado'
                  : 'Nenhum usuário desativado'
            }
            description={
              search.trim()
                ? 'A busca cobre nome e e-mail. Confira também a outra aba.'
                : tab === 'active'
                  ? 'Crie o primeiro usuário do BFF para que alguém possa criar e aprovar conteúdo.'
                  : 'Ninguém foi desativado. Quem for desativado aparece aqui e pode voltar.'
            }
            action={
              tab === 'active' && !search.trim() && privileges.SETTINGS.USERS.CREATE ? (
                <UTButton
                  fitContent
                  text='Novo usuário'
                  icon='Arrows.Plus'
                  onClick={() => {
                    navigate('/settings/users/create');
                  }}
                />
              ) : undefined
            }
          />
        ) : (
          <div className={gridStyle}>
            {userList.map(user => (
              <UserCard
                key={user.id}
                user={user}
                roles={user.roles}
                canManagePermissions={privileges.SETTINGS.USERS.MANAGE_PERMISSIONS}
                /*
                  Both gates on purpose: the flag says the panel offers it, the permission says the
                  BFF allows it. `user:delete` is super_admin only, so the flag alone would render
                  a button that answers 403.
                */
                canDelete={tab === 'active' && privileges.SETTINGS.USERS.DELETE && canDeleteUsers}
                canRestore={
                  tab === 'inactive' && privileges.SETTINGS.USERS.DELETE && canDeleteUsers
                }
                onPermissions={setPermissionsUser}
                onView={setViewingUser}
                onDelete={target => {
                  setDeleteError(null);
                  setDeletingUser(target);
                }}
                onRestore={target => {
                  bringUserBack.mutate(target.id);
                }}
              />
            ))}
          </div>
        )}
      </div>

      {viewingUser ? (
        <UserDetailsModal
          open
          profile={viewingProfile ?? null}
          isLoading={isLoadingProfile}
          onClose={() => {
            setViewingUser(null);
          }}
        />
      ) : null}

      {permissionsUser ? (
        <ManagePermissionsModal
          open
          userId={permissionsUser.id}
          onClose={() => {
            setPermissionsUser(null);
          }}
          onSuccess={() => {
            void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
          }}
        />
      ) : null}

      {/* Rendered only while there is a user: UTModal keeps its content in the a11y tree even
          when closed, which otherwise exposes an empty "Excluir usuário" dialog. */}
      {deletingUser ? (
        <DeleteUserModal
          open
          user={deletingUser}
          isDeleting={removeUser.isPending}
          error={deleteError}
          onClose={() => {
            setDeletingUser(undefined);
            setDeleteError(null);
          }}
          onConfirm={target => {
            removeUser.mutate(target.id);
          }}
        />
      ) : null}
    </MainContent>
  );
};

export { UserList };
